import React, { useMemo, useState } from 'react';
import { EventCard } from './EventCard';
import { StarIcon, MusicIcon, PaletteIcon, UtensilsIcon, TrophyIcon, LaptopIcon, UsersIcon, TagIcon, ClockIcon } from 'lucide-react';
export function EventLibrary({
  events,
  onEditEvent,
  onDeleteEvent,
  onToggleFavorite
}) {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Helper function to determine if an event is in the past
  const isEventInPast = event => {
    if (!event.date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of today
    // Parse the event date (YYYY-MM-DD format)
    const [year, month, day] = event.date.split('-').map(num => parseInt(num, 10));
    const eventDate = new Date(year, month - 1, day);
    return eventDate < today;
  };
  // Extract unique categories from events
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    events.forEach(event => {
      if (event.category) {
        uniqueCategories.add(event.category);
      }
    });
    return Array.from(uniqueCategories);
  }, [events]);
  // Predefined categories from form
  const allCategories = [{
    id: 'music',
    label: 'Music',
    icon: <MusicIcon className="w-3 h-3 mr-1" data-id="element-245" />
  }, {
    id: 'art',
    label: 'Art',
    icon: <PaletteIcon className="w-3 h-3 mr-1" data-id="element-246" />
  }, {
    id: 'food',
    label: 'Food & Drink',
    icon: <UtensilsIcon className="w-3 h-3 mr-1" data-id="element-247" />
  }, {
    id: 'sports',
    label: 'Sports',
    icon: <TrophyIcon className="w-3 h-3 mr-1" data-id="element-248" />
  }, {
    id: 'outdoors',
    label: 'Outdoors',
    icon: <div className="w-3 h-3 mr-1" data-id="element-249" />
  }, {
    id: 'tech',
    label: 'Tech',
    icon: <LaptopIcon className="w-3 h-3 mr-1" data-id="element-250" />
  }, {
    id: 'community',
    label: 'Community',
    icon: <UsersIcon className="w-3 h-3 mr-1" data-id="element-251" />
  }, {
    id: 'other',
    label: 'Other',
    icon: <TagIcon className="w-3 h-3 mr-1" data-id="element-252" />
  }];
  // Get category filters to display - only show categories that have events
  const categoryFilters = useMemo(() => {
    return allCategories.filter(category => categories.includes(category.id));
  }, [categories]);
  // Count past events for badge display
  const pastEventsCount = useMemo(() => {
    return events.filter(isEventInPast).length;
  }, [events]);
  const filteredEvents = useMemo(() => {
    if (!events.length) return [];
    // Apply filter
    let filtered = [...events];
    if (filter === 'favorites') {
      filtered = filtered.filter(event => event.isFavorite);
    } else if (filter === 'past') {
      filtered = filtered.filter(isEventInPast);
    } else if (filter !== 'all') {
      filtered = filtered.filter(event => event.category === filter);
    }
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => event.name.toLowerCase().includes(query) || event.location && event.location.toLowerCase().includes(query) || event.notes && event.notes.toLowerCase().includes(query));
    }
    // Sort by date (closest first for upcoming, most recent first for past)
    return filtered.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      if (filter === 'past') {
        // For past events, show most recent first (descending)
        return new Date(b.date) - new Date(a.date);
      } else {
        // For upcoming events, show closest first (ascending)
        return new Date(a.date) - new Date(b.date);
      }
    });
  }, [events, filter, searchQuery]);
  return <div data-id="element-253">
      <div className="mb-6" data-id="element-254">
        <div className="flex items-center justify-between mb-4" data-id="element-255">
          <h2 className="text-lg font-medium" data-id="element-256">My Events</h2>
        </div>
        {/* Search */}
        <div className="mb-4" data-id="element-257">
          <input type="text" placeholder="Search events..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-258" />
        </div>
        {/* Main Filters - All Events, Favorites, and Past Events */}
        <div className="flex space-x-2 overflow-x-auto pb-2 mb-2" data-id="element-259">
          {/* All Events Filter */}
          <button className={`px-3 py-1 text-sm rounded-full whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setFilter('all')} data-id="element-260">
            All Events
          </button>
          {/* Favorites Filter */}
          <button className={`px-3 py-1 text-sm rounded-full whitespace-nowrap flex items-center ${filter === 'favorites' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setFilter('favorites')} data-id="element-261">
            <StarIcon className={`w-3 h-3 mr-1 ${filter === 'favorites' ? 'text-white' : 'text-yellow-500 fill-yellow-500'}`} data-id="element-262" />
            Favorites
          </button>
          {/* Past Events Filter */}
          <button className={`px-3 py-1 text-sm rounded-full whitespace-nowrap flex items-center ${filter === 'past' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setFilter('past')} data-id="element-263">
            <ClockIcon className={`w-3 h-3 mr-1 ${filter === 'past' ? 'text-white' : 'text-gray-500'}`} data-id="element-264" />
            Past Events
            {pastEventsCount > 0 && <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${filter === 'past' ? 'bg-white text-blue-600' : 'bg-gray-200 text-gray-700'}`} data-id="element-265">
                {pastEventsCount}
              </span>}
          </button>
        </div>
        {/* Category Filters - Below main filters */}
        {categoryFilters.length > 0 && filter !== 'past' && <div className="flex flex-wrap gap-2 mb-4" data-id="element-266">
            {categoryFilters.map(category => <button key={category.id} className={`px-3 py-1 text-sm rounded-full whitespace-nowrap flex items-center ${filter === category.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setFilter(category.id)} data-id="element-267">
                {filter === category.id ? <span className="w-3 h-3 mr-1" data-id="element-268">{category.icon}</span> : category.icon}
                {category.label}
              </button>)}
          </div>}
      </div>
      {/* Event List */}
      <div data-id="element-269">
        {filter === 'past' && <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md" data-id="element-270">
            <p className="text-sm text-gray-600" data-id="element-271">
              Showing your past events. These are events with dates that have
              already passed.
            </p>
          </div>}
        {filteredEvents.length === 0 ? <div className="text-center py-8" data-id="element-272">
            <p className="text-gray-500" data-id="element-273">No events found</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-274">
              {events.length === 0 ? 'Start by adding your first event' : filter === 'past' ? "You don't have any past events yet" : 'Try adjusting your filters'}
            </p>
          </div> : filteredEvents.map(event => <EventCard key={event.id} event={event} onEditEvent={onEditEvent} onDeleteEvent={onDeleteEvent} onToggleFavorite={onToggleFavorite} data-id="element-275" />)}
      </div>
    </div>;
}